import { useState, useCallback, useRef, useMemo } from 'react';
import { makeStyles, tokens, mergeClasses, MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { ShieldLockRegular } from '@fluentui/react-icons';
import { PanelHeader, type PanelState } from './PanelHeader';
import { WorkflowDesigner } from './WorkflowDesigner';
import { CodeEditor } from './CodeEditor';
import { ExecutionModeBanner } from './ExecutionModeBanner';
import { FeatureErrorBoundary } from '@/components/ErrorBoundary';
import { useEditorRunStore } from '@/stores/editorRunStore';
import type { WorkflowDefinition } from '@/types/workflowDefinition';

type ActiveFile = 'workflow.json' | 'connections.json';

type DesignerLayoutProps = {
  canvasState: PanelState;
  codeState: PanelState;
  onCanvasStateChange: (state: PanelState) => void;
  onCodeStateChange: (state: PanelState) => void;
  activeFile: ActiveFile;
  onFileChange: (file: ActiveFile) => void;
  hasConnections: boolean;
  parseError?: string | null;
  /** Callback when user requests to generate a workflow from empty canvas */
  onGenerateWorkflow?: ((prompt: string) => void) | undefined;
  /** Whether the chat sidebar is open */
  isChatSidebarOpen?: boolean;
  /** Toggle code panel visibility */
  onToggleCode?: () => void;
  /** Toggle chat sidebar */
  onToggleChat?: () => void;

  /** Open run workflow dialog */
  onRun?: () => void;
  /** Whether workflow has unsaved changes */
  isDirty?: boolean;
  /** Whether save is in progress */
  isSaving?: boolean;
  /** Trigger save */
  onSave?: () => void;
  /** Whether validation is in progress (shown during save) */
  isValidating?: boolean;
  /** Whether this is a conversational (A2A) workflow */
  isConversational?: boolean;
  /** Start a new chat (for conversational workflows) */
  onStartChat?: () => void;
  /** Current workflow for dirty node detection in execution mode */
  workflow?: WorkflowDefinition | null;
  /** Called when user exits debug mode */
  onExitDebugMode?: () => void;
  /** Whether draft mode is enabled */
  draftModeEnabled?: boolean;
  /** Workflow name (required for draft mode autosave) */
  workflowName?: string;
  /**
   * Stable identity for the current workflow within the full route hierarchy
   * (tenant/location/project/app/workflow). Used as the key on `<WorkflowDesigner>`
   * so its component-local state remounts whenever any of these change, not just
   * when the workflow name changes (two workflows in different apps can share names).
   */
  routeIdentityKey?: string;
  /** Callback to trigger validation */
  onValidate?: () => void;
  /** Number of validation errors (0 = valid, undefined = not validated) */
  validationErrorCount?: number;
  /** When true, hides the dock toolbar and shows a read-only indicator */
  isPermissionReadOnly?: boolean;
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  canvasPanel: {
    flex: 1,
    minWidth: 0,
  },
  codePanel: {
    display: 'flex',
    flexDirection: 'column',
  },
  codePanelNormal: {
    // Width set via inline style for resizing
  },
  codePanelMaximized: {
    flex: 1,
  },
  panelContent: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  minimizedPanel: {
    flex: 'none',
  },
  divider: {
    width: '4px',
    backgroundColor: tokens.colorNeutralStroke1,
    cursor: 'col-resize',
    flexShrink: 0,
    transition: 'background-color 0.1s ease',
    ':hover': {
      backgroundColor: tokens.colorBrandStroke1,
    },
  },
  dividerDragging: {
    backgroundColor: tokens.colorBrandStroke1,
  },
  hidden: {
    display: 'none',
  },
  readOnlyBanner: {
    flexShrink: 0,
  },
  readOnlyIcon: {
    marginRight: tokens.spacingHorizontalSNudge,
    verticalAlign: 'text-bottom',
  },
});

const DEFAULT_CODE_WIDTH = 500;
const MIN_CANVAS_WIDTH = 300;
const MIN_CODE_WIDTH = 300;

export const DesignerLayout = ({
  canvasState,
  codeState,
  onCanvasStateChange,
  onCodeStateChange,
  activeFile,
  onFileChange,
  hasConnections,
  parseError,
  onGenerateWorkflow,
  isChatSidebarOpen,
  onToggleCode,
  onToggleChat,
  onRun,
  isDirty,
  isSaving,
  onSave,
  isValidating,
  isConversational,
  onStartChat,
  workflow,
  onExitDebugMode,
  draftModeEnabled = false,
  workflowName,
  routeIdentityKey,
  onValidate,
  validationErrorCount,
  isPermissionReadOnly = false,
}: DesignerLayoutProps) => {
  const styles = useStyles();
  const [codeWidth, setCodeWidth] = useState(DEFAULT_CODE_WIDTH);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasPanelRef = useRef<HTMLDivElement>(null);
  const containerWidthRef = useRef(0);

  // Get execution mode state and handlers
  const {
    isExecutionMode,
    runStateVersion,
    getNodeExecutions,
    agentIterations: debugAgentIterations,
    setAgentIteration,
    getAgentIterationsData,
  } = useEditorRunStore();

  // Get node executions for debug mode rendering
  const debugNodeExecutions = useMemo(() => {
    if (!isExecutionMode) return undefined;
    return getNodeExecutions();
    // runStateVersion is not used directly but triggers recalculation when
    // streaming updates change the underlying run state (getNodeExecutions is stable).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExecutionMode, getNodeExecutions, runStateVersion]);

  // Get agent iterations data for debug mode (tool invocation status)
  const debugAgentIterationsData = useMemo(() => {
    if (!isExecutionMode) return undefined;
    return getAgentIterationsData();
  }, [isExecutionMode, getAgentIterationsData]);

  // Handler for iteration changes in debug mode
  const handleDebugIterationChange = useCallback((agentName: string, iteration: number) => {
    setAgentIteration(agentName, iteration);
  }, [setAgentIteration]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    if (containerRef.current) {
      containerWidthRef.current = containerRef.current.getBoundingClientRect().width;
    }

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newCodeWidth = containerRect.right - moveEvent.clientX;

      // Clamp to min/max values
      const maxCodeWidth = containerRect.width - MIN_CANVAS_WIDTH;
      const clampedWidth = Math.max(MIN_CODE_WIDTH, Math.min(maxCodeWidth, newCodeWidth));

      setCodeWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  // Handle canvas panel state changes
  const handleCanvasMinimize = () => onCanvasStateChange('minimized');
  const handleCanvasMaximize = () => onCanvasStateChange('maximized');
  const handleCanvasRestore = () => onCanvasStateChange('normal');

  // Handle code panel state changes
  const handleCodeMinimize = () => onCodeStateChange('minimized');
  const handleCodeMaximize = () => onCodeStateChange('maximized');
  const handleCodeRestore = () => onCodeStateChange('normal');

  // File dropdown options
  const fileOptions = [
    { value: 'workflow.json', label: 'workflow.json' },
    { value: 'connections.json', label: 'connections.json', disabled: !hasConnections },
  ];

  // Determine visibility and sizes based on states
  const showCanvas = codeState !== 'maximized';
  const showCode = canvasState !== 'maximized';
  const showDivider = canvasState === 'normal' && codeState === 'normal';

  // Calculate canvas flex based on states
  const getCanvasPanelClass = () => {
    if (canvasState === 'minimized') return mergeClasses(styles.panel, styles.minimizedPanel);
    if (canvasState === 'maximized') return mergeClasses(styles.panel, styles.canvasPanel);
    return mergeClasses(styles.panel, styles.canvasPanel);
  };

  // Calculate code panel style
  const getCodePanelStyle = (): React.CSSProperties => {
    if (codeState === 'maximized') return { flex: 1 };
    if (codeState === 'minimized') return { width: 'auto' };
    // When canvas is minimized, code should take full remaining width
    if (canvasState === 'minimized') return { flex: 1 };
    return { width: codeWidth };
  };

  return (
    <div ref={containerRef} className={styles.container}>
      {/* Canvas Panel */}
      {showCanvas && (
        <div className={getCanvasPanelClass()}>
          {/* Only show header when both panels are visible (not maximized) */}
          {canvasState !== 'maximized' && (
            <PanelHeader
              title="Canvas"
              state={canvasState}
              onMinimize={handleCanvasMinimize}
              onMaximize={handleCanvasMaximize}
              onRestore={handleCanvasRestore}
              icon="canvas"
            />
          )}
          {canvasState !== 'minimized' && (
            <div ref={canvasPanelRef} className={styles.panelContent}>
              {isPermissionReadOnly && (
                <MessageBar intent="warning" layout="singleline" className={styles.readOnlyBanner}>
                  <MessageBarBody>
                    <ShieldLockRegular className={styles.readOnlyIcon} aria-hidden="true" />
                    <MessageBarTitle>Read-only</MessageBarTitle>
                    You don't have the correct permissions to edit this workflow.
                  </MessageBarBody>
                </MessageBar>
              )}
              <ExecutionModeBanner workflow={workflow ?? null} onExit={onExitDebugMode} />
              <FeatureErrorBoundary componentName="WorkflowCanvas">
                <WorkflowDesigner
                  key={routeIdentityKey ?? workflowName}
                  isChatSidebarOpen={isChatSidebarOpen}
                  // Command bar props
                  isCodeVisible={codeState !== 'minimized'}
                  isDirty={isDirty ?? false}
                  isSaving={isSaving ?? false}
                  isConversational={isConversational ?? false}
                  isValidating={isValidating ?? false}
                  commandBarContainerRef={canvasPanelRef}
                  commandBarTopOffset={isExecutionMode ? 40 : isPermissionReadOnly ? 36 : 0}
                  // Draft mode props
                  draftModeEnabled={draftModeEnabled}
                  workflowName={workflowName}
                  {...(onGenerateWorkflow && !isPermissionReadOnly && { onGenerateWorkflow })}
                  {...(onToggleCode && !isPermissionReadOnly && { onToggleCode })}
                  {...(onToggleChat && !isPermissionReadOnly && { onToggleChat })}

                  {...(onRun && !isPermissionReadOnly && { onRun })}
                  {...(onSave && !isPermissionReadOnly && { onSave })}
                  {...(onStartChat && !isPermissionReadOnly && { onStartChat })}
                  {...(onValidate && !isPermissionReadOnly && { onValidate })}
                  {...(validationErrorCount !== undefined && { validationErrorCount })}

                  {...(debugNodeExecutions && debugNodeExecutions.length > 0 ? {
                    nodeExecutions: debugNodeExecutions,
                    agentIterations: debugAgentIterations,
                    agentIterationsData: debugAgentIterationsData,
                    onIterationChange: handleDebugIterationChange,
                  } : {})}
                />
              </FeatureErrorBoundary>
            </div>
          )}
        </div>
      )}

      {/* Divider */}
      {showDivider && (
        <div
          className={mergeClasses(styles.divider, isDragging && styles.dividerDragging)}
          onMouseDown={handleMouseDown}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panels"
          aria-valuenow={containerWidthRef.current > 0 ? Math.round((codeWidth / containerWidthRef.current) * 100) : 50}
          aria-valuemin={0}
          aria-valuemax={100}
          tabIndex={0}
        />
      )}

      {/* Code Panel */}
      {showCode && (
        <div
          className={mergeClasses(styles.panel, styles.codePanel)}
          style={getCodePanelStyle()}
        >
          <PanelHeader
            title={activeFile}
            state={codeState}
            onMinimize={handleCodeMinimize}
            onMaximize={handleCodeMaximize}
            onRestore={handleCodeRestore}
            dropdown={{
              value: activeFile,
              options: fileOptions,
              onChange: (value) => onFileChange(value as ActiveFile),
            }}
            errorBadge={parseError ?? null}
            icon="code"
            showCanvasAction={codeState === 'maximized'}
            onShowCanvas={handleCodeRestore}
            isDirty={isDirty}
            isSaving={isSaving}
            onSave={onSave}
          />
          {codeState !== 'minimized' && (
            <div className={styles.panelContent}>
              <FeatureErrorBoundary componentName="CodeEditor">
                <CodeEditor hideHeader />
              </FeatureErrorBoundary>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
